import type { Hub } from '@/data/Hubs/types'
import type { Field } from '@/modules/Project/Fields/types'
import type { Entity } from '@/modules/Project/types'
import type { Project } from '@/modules/Projects/useProjects'
import { invariant } from '@/shared/utils/typeAssertions'
import { buildDataField, buildSingleSelectField, buildTextField } from '@/test/factories/field'

/**
 * Creates an optimistic entity to be used when uploading files to a hub.
 */
export function getOptimisticHubEntity(props: {
  hubProject: Hub
  fileProject: Project
  path: string
}): Entity {
  const { hubProject, fileProject } = props

  const fields = new Map<string, Field>()

  const entity: Entity = {
    activeViewIds: [fileProject.mainViewId || 'main'],
    fields,
    id: crypto.randomUUID(),
    parentEntityId: hubProject.entityId,
    projectId: fileProject.id,
  }

  let filePropertyId: string | undefined
  let pathPropertyId: string | undefined
  let documentTypePropertyId: string | undefined
  let summaryPropertyId: string | undefined

  for (const property of fileProject.properties) {
    switch (property.name) {
      case 'file':
        filePropertyId = property.id
        break
      case 'path':
        pathPropertyId = property.id
        break
      case 'document type':
        documentTypePropertyId = property.id
        break
      case 'summary':
        summaryPropertyId = property.id
        break
    }
  }

  invariant(filePropertyId)
  invariant(pathPropertyId)
  invariant(documentTypePropertyId)
  invariant(summaryPropertyId)

  const now = new Date().toISOString()
  const filename = props.path.split('/').pop()

  const file = buildDataField({
    entityId: entity.id,
    status: 'uploading',
    manualFilename: filename,
    createdAt: now,
    updatedAt: now,
    propertyId: filePropertyId,
  })

  const path = buildTextField({
    entityId: entity.id,
    manualValue: props.path,
    createdAt: now,
    updatedAt: now,
    propertyId: pathPropertyId,
  })

  const summary = buildTextField({
    entityId: entity.id,
    manualValue: '',
    createdAt: now,
    updatedAt: now,
    propertyId: summaryPropertyId,
  })

  const documentType = buildSingleSelectField({
    entityId: entity.id,
    toolValue: ['anything-but-invalid'],
    propertyId: documentTypePropertyId,
  })

  entity.fields.set(file.propertyId, file)
  entity.fields.set(path.propertyId, path)
  entity.fields.set(documentType.propertyId, documentType)
  entity.fields.set(summary.propertyId, summary)

  return entity
}
